import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row, Button } from 'reactstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';

import DayPicker from '../components/DayPicker';
import Calendar from '../components/Calendar';
import Layout from '../components/Layout';
import UpcomingEvents from '../components/UpcomingEvents';
import api from '../services/api';
import { getWeekStart, toDate } from '../utils/date';

const Main = () => {
  const calendarRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [hours, setHours] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchHours = useCallback(async (date) => {
    try {
      const res = await api.get(`/business-hours/?date=${moment(date).format('YYYY-MM-DD')}`);
      setHours(res.data);
    } catch (error) {
      console.error('Failed to load business hours:', error);
    }
  }, []);

  useEffect(() => {
    fetchHours(selectedDate);
  }, [selectedDate, fetchHours]);

  useEffect(() => {
    if (calendarRef.current && typeof calendarRef.current.getApi === 'function') {
      calendarRef.current.getApi().gotoDate(selectedDate);
    }
  }, [selectedDate]);

  const triggerRefresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const handleDateChange = useCallback((date) => {
    const normalizedDate = toDate(date);
    setSelectedDate(normalizedDate);
    setWeekStart(getWeekStart(normalizedDate));
  }, []);

  const handleEventsUpdated = useCallback((date) => {
    if (date) {
      handleDateChange(date);
    }
    triggerRefresh();
  }, [handleDateChange, triggerRefresh]);

  const handleToday = useCallback(() => {
    handleDateChange(new Date());
  }, [handleDateChange]);

  const changeWeek = useCallback((offset) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + offset * 7);
    handleDateChange(next);
  }, [weekStart, handleDateChange]);

  return (
    <Layout>
      <Row className="m-0 flex-grow-1 h-100">
        <Col sm="4" className="m-0 p-0 border-start bg-dark bg-opacity-10">
          <UpcomingEvents
            selectedDate={selectedDate}
            onSubmit={handleEventsUpdated}
            onToday={handleToday}
            refreshToken={refreshToken}
          />
        </Col>
        <Col className="h-100 d-flex flex-column">
          <Row className="justify-content-center bg-white bg-opacity-10 p-2">
            <Col xs="1" className="d-flex justify-content-start m-0 p-0">
              <Button color="secondary" onClick={() => changeWeek(-1)}>
                <FaChevronLeft />
              </Button>
            </Col>
            <Col xs="10" className="d-flex justify-content-center m-0 p-0">
              <DayPicker
                startDate={weekStart}
                currentDate={selectedDate}
                onDateSelected={handleDateChange}
              />
            </Col>
            <Col xs="1" className="d-flex justify-content-end m-0 p-0">
              <Button color="secondary" onClick={() => changeWeek(1)}>
                <FaChevronRight />
              </Button>
            </Col>
          </Row>
          <Row className="border-start flex-grow-1 h-100">
            <Calendar
              calendarRef={calendarRef}
              selectedDate={selectedDate}
              hours={hours}
              bookingDate={selectedDate}
              onToday={handleToday}
              onSubmit={handleEventsUpdated}
              refreshToken={refreshToken}
            />
          </Row>
        </Col>
      </Row>
    </Layout>
  );
};

export default Main;
